import { configureStore } from "@reduxjs/toolkit";
import {
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import persistReducer from "redux-persist/es/persistReducer";
import rootReducer from "./reducers/rootReducer";
// ── Role-namespaced cross-tab seed ──────────────────────────────────────────
// Each role group stores its seed under a separate localStorage key so that
// two tabs logged in as different roles don't overwrite each other's seed.
//
// Seed keys: persist:seed:main  (recruiter / agency / user)
//            persist:seed:client
//            persist:seed:applicant

/** Map a URL path to its seed key (for reading in a new tab). */
function getSeedKeyFromPath(): string | null {
  const path = window.location.pathname;
  if (path.startsWith("/client-dashboard")) return "persist:seed:client";
  if (
    path.startsWith("/applicant-dashboard") ||
    path.startsWith("/applicantprofile") ||
    path.startsWith("/jobs") ||
    path.startsWith("/resume-") ||
    path.startsWith("/update-applicant-resume") ||
    path.startsWith("/ai-practice-interview") ||
    path.startsWith("/written-assessment") ||
    path.startsWith("/code-editor") ||
    path.startsWith("/under-review") ||
    path.startsWith("/related-jobs") ||
    path.startsWith("/interview-module") ||
    path.startsWith("/start-interview") ||
    path.startsWith("/interview-panel-ready") ||
    path.startsWith("/practice-assessment") ||
    path.startsWith("/interview-instruction") ||
    path.startsWith("/ai-interview-guideline") ||
    path.startsWith("/ai-practice-interview-guideline") ||
    path.startsWith("/schedule-confirmation")
  )
    return "persist:seed:applicant";
  if (
    path.startsWith("/dashboard") ||
    path.startsWith("/view-jobs-data") ||
    path.startsWith("/view-invited-jobs-data") ||
    path.startsWith("/view-requested-jobs-data") ||
    path.startsWith("/view-demo-jobs-data") ||
    path.startsWith("/company-details") ||
    path.startsWith("/meeting") ||
    path.startsWith("/payment-") ||
    path.startsWith("/credit-success") ||
    path.startsWith("/contact-sales") ||
    path.startsWith("/interview-analyser") ||
    path.startsWith("/inteview-reshedule") ||
    path.startsWith("/try-haire/interview") ||
    path.startsWith("/myrequirements/") ||
    path.startsWith("/job-details")
  )
    return "persist:seed:main";
  // Shared private paths (accessible by multiple roles) — try main as fallback
  if (
    path.startsWith("/onboarding") ||
    path.startsWith("/check-out") ||
    path.startsWith("/congratulations") ||
    path.startsWith("/better-luck") ||
    path.startsWith("/confirm-information") ||
    path.startsWith("/account-verification") ||
    path.startsWith("/help/troubleshoot-microphone")
  )
    return "persist:seed:main";
  return null; // public / login paths → no seeding
}

/** Extract role from persisted Redux state to determine the seed key for writing. */
function getSeedKeyFromState(value: string): string {
  try {
    const parsed = JSON.parse(value);
    const auth = JSON.parse(parsed.auth || "{}");
    const role = Number(auth.user?.role);
    if (role === 4 || role === 3) return "persist:seed:client";
    if (role === 2) return "persist:seed:applicant";
  } catch {
    // ignore parse errors
  }
  return "persist:seed:main"; // recruiter (1), agency (5), or unknown
}

// All three possible seed keys, for cleanup during logout.
const ALL_SEED_KEYS = [
  "persist:seed:main",
  "persist:seed:client",
  "persist:seed:applicant",
];

// ── Link-seed: snapshot parent session on link interaction ──────────────────
// When the user right-clicks (or clicks) a link, we snapshot the current tab's
// session into localStorage so the new tab can read the EXACT parent session.
const LINK_SEED_KEY = "persist:link-seed";
const LINK_SEED_TS_KEY = "persist:link-seed-ts";
const LINK_SEED_MAX_AGE_MS = 10_000; // 10 seconds

/**
 * Call once from App.js to install the global mousedown listener.
 * On every link interaction the current tab's session is written to localStorage
 * so that a subsequently opened new tab reads the correct parent session.
 */
export function setupLinkSeedListener(): () => void {
  const handler = (e: MouseEvent) => {
    const link = (e.target as HTMLElement)?.closest?.("a[href]");
    if (!link) return;
    const state = sessionStorage.getItem("persist:root");
    if (state) {
      localStorage.setItem(LINK_SEED_KEY, state);
      localStorage.setItem(LINK_SEED_TS_KEY, Date.now().toString());
    }
  };
  document.addEventListener("mousedown", handler, true);
  return () => document.removeEventListener("mousedown", handler, true);
}

// Hybrid storage adapter for redux-persist.
// Seeding priority for new tabs:
//   1. persist:link-seed  (mousedown snapshot — most accurate)
//   2. window.opener      (right-click → Open in new tab, if browser allows)
//   3. Role-namespaced localStorage (fallback for direct URL entry)
const crossTabStorage = {
  getItem: (key: string): Promise<string | null> => {
    // 1. Always prefer this tab's own sessionStorage
    const sessionValue = sessionStorage.getItem(key);
    if (sessionValue) return Promise.resolve(sessionValue);

    // HA1-I2063: the public Try Haire demo entry must NEVER inherit an existing
    // logged-in session from another tab (link-seed / window.opener / role seed).
    // The demo builds its own throwaway session via verifyTryOtp after OTP. The
    // in-demo interview path (/try-haire/interview) is excluded so it can still
    // rehydrate its own freshly-created demo session within the same tab.
    const path = window.location.pathname;
    if (
      path.startsWith("/try-haire") &&
      !path.startsWith("/try-haire/interview")
    ) {
      return Promise.resolve(null);
    }

    // 2. Try link-seed (written by mousedown handler in the parent tab)
    try {
      const linkSeed = localStorage.getItem(LINK_SEED_KEY);
      const linkSeedTs = localStorage.getItem(LINK_SEED_TS_KEY);
      if (
        linkSeed &&
        linkSeedTs &&
        Date.now() - parseInt(linkSeedTs, 10) < LINK_SEED_MAX_AGE_MS
      ) {
        sessionStorage.setItem(key, linkSeed);
        // Clean up to prevent stale reuse
        localStorage.removeItem(LINK_SEED_KEY);
        localStorage.removeItem(LINK_SEED_TS_KEY);
        return Promise.resolve(linkSeed);
      }
    } catch {
      // ignore
    }

    // 3. Try window.opener (browser may set it for same-origin new tabs)
    try {
      if (window.opener && !window.opener.closed) {
        const openerValue = window.opener.sessionStorage?.getItem(key);
        if (openerValue) {
          sessionStorage.setItem(key, openerValue);
          return Promise.resolve(openerValue);
        }
      }
    } catch {
      // Cross-origin, opener closed, or security restriction
    }

    // 4. Fall back to role-namespaced localStorage seed
    const seedKey = getSeedKeyFromPath();
    if (seedKey) {
      const localValue = localStorage.getItem(seedKey);
      if (localValue) {
        sessionStorage.setItem(key, localValue);
        return Promise.resolve(localValue);
      }
    }

    return Promise.resolve(null);
  },
  setItem: (key: string, value: string): Promise<void> => {
    sessionStorage.setItem(key, value);
    // Also update the role-namespaced seed (fallback for direct URL entry)
    const seedKey = getSeedKeyFromState(value);
    localStorage.setItem(seedKey, value);
    return Promise.resolve();
  },
  removeItem: (key: string): Promise<void> => {
    sessionStorage.removeItem(key);
    // Clear all seeds on removal (logout / purge)
    ALL_SEED_KEYS.forEach((k) => localStorage.removeItem(k));
    localStorage.removeItem(LINK_SEED_KEY);
    localStorage.removeItem(LINK_SEED_TS_KEY);
    return Promise.resolve();
  },
};

const persistConfig = {
  key: "root",
  storage: crossTabStorage,
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          "company/updateCompanyDetails",
        ],
        ignoredPaths: ["company.details.profile_pic"],
      },
    }),
});

export const persistor = persistStore(store);
